import { Routes } from '@angular/router';
import {
  deletedAccountRedirectGuard,
  destinationHandlerGuard,
  initAuthGuard,
  initTranslationGuard,
  injectSsrContentGuard,
  InstallationDeactivateGuard,
  isAuthenticatedGuard,
  OrderSummaryDeactivateGuard,
  planHandlerGuard,
  PurchaseSuccessDeactivateGuard,
  seoHandlingGuard,
  setupCacheGuard,
  setupCurrenciesGuard,
  setupFaqGuard,
  socialSigninGuard,
  supportedDevicesGuard,
  updateTitleFaviconGuard,
} from '@gb-webapp/gb-app';
import { authModalGuard } from '../pages/auth/auth-modal.guard';
import { legalRoutes } from '../pages/legal/legal.routes';
export const layoutRoutes: Routes = [
  {
    path: '404',
    loadComponent: () =>
      import('../../../../../libs/gb-app/src/lib/components/not-found/not-found.component').then(
        (m) => m.NotFoundComponent
      ),
  },
  {
    path: '',
    canActivate: [setupCacheGuard],
    children: [
      {
        path: '',
        canActivate: [
          injectSsrContentGuard,
          updateTitleFaviconGuard,
          initAuthGuard,
          setupFaqGuard,
          supportedDevicesGuard,
          initTranslationGuard,
          seoHandlingGuard,
          setupCurrenciesGuard,
        ],
        loadComponent: () => import('./layout.component').then((m) => m.LayoutComponent),
        canActivateChild: [seoHandlingGuard],
        children: [
          {
            // OAuth callback route - DO NOT use for navigatio
            path: 'auth/signin',
            canActivate: [socialSigninGuard],
            loadComponent: () =>
              import(
                '../../../../../libs/gb-app/src/lib/components/auth-loader/auth-loader.component'
              ).then((m) => m.AuthLoaderComponent),
          },
          {
            path: 'auth/sign-up',
            canActivate: [authModalGuard],
            loadComponent: () =>
              import('../pages/auth/auth-wrapper.component').then((m) => m.AuthWrapperComponent),
          },
          // User-facing sign-in modal route
          {
            path: 'auth/sign-in',
            canActivate: [authModalGuard],
            loadComponent: () =>
              import('../pages/auth/auth-wrapper.component').then((m) => m.AuthWrapperComponent),
          },
          {
            path: 'settings',
            canActivate: [isAuthenticatedGuard],
            loadChildren: () =>
              import('../pages/settings/settings.routes').then((m) => m.settingsRoutes),
          },
          {
            path: 'settings/delete/success',
            canActivate: [deletedAccountRedirectGuard],
            loadComponent: () =>
              import(
                '../pages/settings/components/delete-account-success/delete-account-success.component'
              ).then((m) => m.DeleteAccountSuccessComponent),
          },
          {
            path: 'my-esims',
            canActivate: [isAuthenticatedGuard],
            loadChildren: () =>
              import('../pages/my-esims/my-esims.routes').then((m) => m.myEsimsRoutes),
          },
          {
            path: 'installation',
            loadComponent: () =>
              import('../pages/my-esims/installation-details/installation-details.component').then(
                (m) => m.InstallationDetailsComponent
              ),
            canDeactivate: [InstallationDeactivateGuard],
          },
          {
            path: 'my-esims/installation-details',
            loadComponent: () =>
              import('../pages/my-esims/installation-details/installation-details.component').then(
                (m) => m.InstallationDetailsComponent
              ),
          },
          {
            path: 'reward-points',
            loadComponent: () =>
              import('../pages/reward-points/reward-points.component').then(
                (m) => m.RewardPointsComponent
              ),
          },
          {
            path: 'verification',
            canActivate: [isAuthenticatedGuard],
            loadComponent: () =>
              import('../pages/identity-verification/identity-verification.component').then(
                (m) => m.IdentityVerificationComponent
              ),
          },
          {
            path: 'faq',
            loadComponent: () => import('../pages/faq/faq.component').then((m) => m.FaqComponent),
          },
          {
            path: 'contact-us',
            loadComponent: () =>
              import('../pages/help/help.component').then((m) => m.HelpComponent),
          },
          {
            path: 'esim-compatible-devices',
            loadComponent: () =>
              import('../pages/supported-devices/supported-devices.component').then(
                (m) => m.SupportedDevicesComponent
              ),
          },
          {
            path: 'purchases',
            canActivate: [isAuthenticatedGuard],
            loadComponent: () =>
              import('../pages/billing-and-payments/billing-and-payments.component').then(
                (m) => m.BillingAndPaymentsComponent
              ),
          },
          {
            path: 'purchases/:orderId',
            loadComponent: () =>
              import('../pages/shared/purchase-details/purchase-details.component').then(
                (m) => m.PurchaseDetailsComponent
              ),
            data: {
              fromPurchases: 'true',
            },
          },
          {
            path: 'supported-devices',
            loadComponent: () =>
              import('../pages/supported-devices/supported-devices.component').then(
                (m) => m.SupportedDevicesComponent
              ),
          },
          {
            path: 'affiliate-program',
            loadComponent: () =>
              import('../pages/affiliate-program/affiliate-program.component').then(
                (m) => m.AffiliateProgramComponent
              ),
          },
          {
            path: 'how-it-works',
            loadComponent: () =>
              import('../pages/how-it-works/how-it-works.component').then(
                (m) => m.HowItWorksComponent
              ),
          },
          {
            path: 'destination',
            loadComponent: () =>
              import('./../pages/buy-plan/destination/destination-wrapper.component').then(
                (m) => m.DestinationWrapperComponent
              ),
            data: {
              hideViewAllButton: false,
              showAllCountries: false,
              selectedToggle: 'Countries',
            },
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('./../pages/buy-plan/destination/destination.component').then(
                    (m) => m.DestinationComponent
                  ),
              },
              {
                path: 'countries',
                loadComponent: () =>
                  import('./../pages/buy-plan/destination/destination.component').then(
                    (m) => m.DestinationComponent
                  ),
              },
              {
                path: 'regions',
                loadComponent: () =>
                  import('./../pages/buy-plan/destination/destination.component').then(
                    (m) => m.DestinationComponent
                  ),
              },
            ],
          },
          {
            path: 'buy-plan',
            loadChildren: () =>
              import('../pages/buy-plan/buy-plan.routes').then((m) => m.buyPlanRoutes),
          },
          {
            path: '',
            loadComponent: () =>
              import('../pages/home/home.component').then((m) => m.HomeComponent),
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('../pages/home/destination-selector-wrapper.component').then(
                    (m) => m.DestinationSelectorWrapperComponent
                  ),
              },
              {
                path: 'countries',
                loadComponent: () =>
                  import('../pages/home/destination-selector-wrapper.component').then(
                    (m) => m.DestinationSelectorWrapperComponent
                  ),
              },
              {
                path: 'regions',
                loadComponent: () =>
                  import('../pages/home/destination-selector-wrapper.component').then(
                    (m) => m.DestinationSelectorWrapperComponent
                  ),
              },
            ],
          },
          ...legalRoutes,
          {
            // Tap 3DS return – opens in popup; notifies opener and closes. Main app stays.
            path: 'payment-status',
            loadComponent: () =>
              import(
                '../pages/buy-plan/order-summary/tap-three-ds-callback/tap-three-ds-callback.component'
              ).then((m) => m.TapThreeDSCallbackComponent),
          },
          {
            path: ':destination',
            canActivate: [destinationHandlerGuard],
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('../pages/buy-plan/plan-list/plan-list.component').then(
                    (m) => m.PlanListComponent
                  ),
              },
              {
                path: ':plan_slug',
                canActivate: [planHandlerGuard],
                loadComponent: () =>
                  import('../pages/buy-plan/plan-list/plan-list.component').then(
                    (m) => m.PlanListComponent
                  ),
              },
              {
                path: ':plan_slug/checkout',
                canActivate: [planHandlerGuard],
                canDeactivate: [OrderSummaryDeactivateGuard],
                loadComponent: () =>
                  import('../pages/buy-plan/order-summary/order-summary.component').then(
                    (m) => m.OrderSummaryComponent
                  ),
              },
              {
                path: ':plan_slug/payment-success',
                canActivate: [planHandlerGuard],
                canDeactivate: [OrderSummaryDeactivateGuard, PurchaseSuccessDeactivateGuard],
                loadComponent: () =>
                  import('../pages/buy-plan/purchase-success/purchase-success.component').then(
                    (m) => m.PurchaseSuccessComponent
                  ),
              },
            ],
          },
        ],
      },
      {
        path: '**',
        loadComponent: () =>
          import(
            '../../../../../libs/gb-app/src/lib/components/not-found/not-found.component'
          ).then((m) => m.NotFoundComponent),
      },
    ],
  },
];
